<template>
	<component
		:is="_isClickoutAnchor ? ClickoutAnchor : 'div'"
		v-if="watchNowOffer"
		:href="clickoutUrl"
		:rel="_isClickoutAnchor ? 'nofollow noopener' : undefined"
		class="watch-now-button"
		:class="{
			'watch-now-button--large': size === 'large',
			'watch-now-button--slim': size === 'slim',
		}"
		@click.stop="handleWatchNow"
		v-observe-visibility="observeVisibilityOptions"
	>
		<BasicButton :tag="_isClickoutAnchor ? 'div' : 'button'" :blocked="true" :color="color" :rounded="rounded">
			<slot name="provider-icon">
				<ProviderIcon
					v-if="!hideIcon && providerShortName"
					size="small"
					:lazy="lazy"
					:providerShortName="providerShortName"
					class="me-1"
				/>
			</slot>

			<slot>{{ watchNowLabel }}</slot>
		</BasicButton>
	</component>
</template>

<script setup lang="ts">
import { type ObjectType } from '@/@types/graphql-types';

import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import BasicButton from '@/ui-components/BasicButton.vue';

import { ClickoutUrl, getClickoutUrlContextsArray, handleClickoutUrl, trackClickout } from '@/helpers/clickout-helper';
import { SnowplowContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { computed } from 'vue';

import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { observableOptions, useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';
import { getClickoutTrackingPlacement } from '@/helpers/composables/useProvideClickoutPlacement';
import { ClickoutType } from '@/interfaces/snowplow/clickout-context';
import { inject } from 'vue';
import { type MaybeRefOrGetter, toValue } from 'vue';
import type { WatchNowOfferFragment } from '@/pages/graphql/fragments/WatchNowOffer.fragment';
import type { Maybe } from '@/helpers/types';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import ClickoutAnchor from '../nav/links/ClickoutAnchor.vue';
import { isJWTVRentalOffer } from '@/features/jwtv-titles-rental/helpers/jwtv-titles-rental-helper';
import { useRentalModal } from '@/features/jwtv-titles-rental/composables/useRentalModal';
import { useAppStore } from '@/stores/pinia/useAppStore';

const props = withDefaults(
	defineProps<{
		titleObjectId: number;
		titleObjectType: ObjectType;
		title?: TitleDetail;
		watchNowOffer?: Maybe<WatchNowOfferFragment>;
		titleSeasonNumber?: number;
		lazy?: boolean;
		size?: 'default' | 'large' | 'slim';
		rounded?: boolean;
		color?: 'secondary' | 'primary' | 'dark';
		inQuickActions?: boolean;
		hideIcon?: boolean;
		overwriteClickTracking?: boolean;
		// Useful for SRs where the offer link is already affiliated or from an ad network
		useClassicUrl?: boolean;
		isSponsoredRecommendation?: boolean;
		sponsoredRecommendationType?: string;
		additionalContexts?: SnowplowContext[];
		isClickoutAnchor?: boolean;
	}>(),
	{
		// @ts-ignore
		watchNowOffer: () => ({}),
		titleSeasonNumber: undefined,
		lazy: true,
		size: 'default',
		rounded: true,
		color: 'secondary',
		inQuickActions: false,
		hideIcon: false,
		overwriteClickTracking: false,
		isSponsoredRecommendation: false,
		sponsoredRecommendationType: 'REGULAR',
		additionalContexts: () => [],
		useClassicUrl: false,
		title: undefined,
	}
);

const emits = defineEmits(['watchNowClicked']);

const { country } = storeToRefs(useLanguageStore());
const { xServeTrack } = storeToRefs(useAppStore());

const watchNowClickoutType = inject<MaybeRefOrGetter<ClickoutType> | undefined>('watchNowClickoutType', undefined);
const { clickoutType, placement } = getClickoutTrackingPlacement('watch_now');

const titleContext = computed(
	() =>
		new SnowplowTitleContextGraphql(
			props.titleObjectId,
			props.titleObjectType,
			props.titleSeasonNumber,
			undefined,
			undefined,
			undefined,
			props.title?.id
		)
);

const isModal = computed(() =>
	['plexplayer', 'justwatchtv', 'ottstudioplayer'].includes(props.watchNowOffer?.package.technicalName || '')
);

const _isClickoutAnchor = computed(() => props.isClickoutAnchor && !isModal.value);

// Offer impression tracking
const { onIntersection } = useOfferImpressionTracking({
	additionalContexts: () => [
		...(props.additionalContexts || []).filter(ctx => ctx.__name !== 'TitleContextGraphql'),
		titleContext.value,
	],
});

const clickoutUrl = computed(() => {
	const url = new ClickoutUrl(props.watchNowOffer)
		.setTitleSourceId(props.title?.id ?? null)
		.skipRedirect(!!props.useClassicUrl)
		.addContexts(props.additionalContexts)
		.addContexts(titleContext)
		.set('uct_country', country.value)
		.set('uct_tryout', 1)
		.updateContext('ClickoutContextGraphql', 'clickoutType', toValue(watchNowClickoutType) ?? clickoutType)
		.updateContext('ClickoutContextGraphql', 'placement', placement)
		.updateContext('TitleContextGraphql', 'jwRawEntityId', props.title?.id)
		.setCanary(xServeTrack.value)
		.setGenre(props.title?.content.genres);

	// Amazon streaming bounty redirects
	if ([9, 119].includes(props.watchNowOffer?.package?.packageId ?? 0)) {
		return url.set('r', url.get('r')!.replace('app.primevideo.com', 'www.primevideo.com'));
	}

	return url;
});

const providerShortName = computed(() => props.watchNowOffer?.package?.shortName ?? undefined);

const { t } = useI18n();
const watchNowLabel = computed(() =>
	props.sponsoredRecommendationType === 'CINEMATIC' && props.isSponsoredRecommendation
		? t('WEBAPP_BUY_YOUR_TICKETS')
		: t('WEBAPP_SEENLIST_WATCHNOW')
);

const playerByTechnicalName: Record<string, () => Promise<any>> = {
	plexplayer: () => import('@/ui-components/video-player-modals/PlexPlayerModal.vue'),
	justwatchtv: () => import('@/ui-components/video-player-modals/JwTVModal.vue'),
	ottstudioplayer: () => import('@/ui-components/video-player-modals/OttPlayerModal.vue'),
};

const observeVisibilityOptions = computed(() => {
	return {
		callback: onVisibilityChanged,
		...observableOptions,
	};
});

function onVisibilityChanged(isVisible: boolean) {
	if (props.watchNowOffer) onIntersection(clickoutUrl.value, isVisible);
}

const { openRentalModal } = useRentalModal();
const client = useNuxtApp().$apolloClients.default!;

function handleWatchNow(event: MouseEvent) {
	emits('watchNowClicked');

	if (!_isClickoutAnchor.value) event.preventDefault();

	if (!props.watchNowOffer) return;

	// JUSWATCH_TV
	if (isModal.value) {
		const isRentOffer = isJWTVRentalOffer(props.watchNowOffer);
		// we trigger clickout first to get the final link context id as it's used to link events especially after page redirects.
		trackClickout(clickoutUrl.value);
		if (isRentOffer) {
			openRentalModal({
				offer: props.watchNowOffer,
				title: props.title!,
				clickoutUrl: clickoutUrl.value,
				overrideRedirectUrl: true,
			});
		} else {
			ModalHelper.openModal(
				playerByTechnicalName[props.watchNowOffer.package.technicalName],
				{
					offer: props.watchNowOffer,
					title: props.title,
					additionalContexts: getClickoutUrlContextsArray(clickoutUrl.value),
				},
				{ cssClass: 'trailer-modal' },
				'',
				true,
				[]
			);
		}
	} else if (!_isClickoutAnchor.value) {
		handleClickoutUrl(clickoutUrl.value, client, false);
	}

	const contexts = [
		...(props.additionalContexts ?? []),
		titleContext.value,
		clickoutUrl.value.contexts.ClickoutContextGraphql,
	];

	if (!props.overwriteClickTracking) {
		TrackingHelper.trackEvent(
			'userinteraction',
			{
				action: 'watchnow_clicked',
				property: props.inQuickActions + '',
			},
			contexts
		);
	}
}
</script>
